import { useEffect } from 'react'
import { Box, Text } from '@chakra-ui/react';
import { Card } from '../shared/Card'
import { usePreviewRender } from '../../hooks/useCommunications'
import { extractApiMessage } from '../../lib/apiError'
import type { EmailSourceRef } from '../../types/communications'

interface EmailPreviewPaneProps {
  subject: string
  bodyMarkdown: string
  source: EmailSourceRef | null
}

/** Debounced live preview of one recipient's rendered subject/HTML (spec §8/§5.4) -- fires
 * `usePreviewRender` (a mutation, not a query, per Task 9's rationale) 400ms after the last
 * subject/body/source edit rather than on every keystroke. */
export function EmailPreviewPane({ subject, bodyMarkdown, source }: EmailPreviewPaneProps) {
  const preview = usePreviewRender()
  const { mutate } = preview

  useEffect(() => {
    if (!source || !bodyMarkdown.trim()) return
    const timer = setTimeout(() => {
      mutate({ subject, bodyMarkdown, source })
    }, 400)
    return () => clearTimeout(timer)
    // Deps are the primitives that actually determine the render output, not the `source`
    // object identity (which the parent may or may not have memoized) -- `mutate` itself is
    // stable across renders (TanStack Query v5), matching the "depend on .mutate, never the
    // whole result" rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, bodyMarkdown, source?.kind, source?.id, JSON.stringify(source?.deliveryModes ?? []), mutate])

  return (
    <Card>
      <Text fontWeight="semibold" mb={2}>Preview</Text>
      {preview.isPending && <Text fontSize="sm" color="fg.muted">Rendering…</Text>}
      {!!preview.error && (
        <Text fontSize="sm" color="danger.fg">
          {extractApiMessage(preview.error, 'Could not render a preview.')}
        </Text>
      )}
      {!!preview.data && (
        <Box borderWidth="1px" borderColor="border" borderRadius="md" p={3}>
          <Text fontWeight="semibold" mb={2}>{preview.data.subject}</Text>
          {/* preview.data.html is rendered server-side by EmailRenderService, whose Markdig
              pipeline runs with DisableHtml() -- any raw HTML the author typed is neutralized
              into literal text before this string is ever produced, and merge-field VALUES are
              HTML-escaped before substitution (spec §6). This is the server's already-sanitized
              output echoed back, not raw user markdown rendered client-side (which
              frontend/CLAUDE.md's react-markdown/no-rehype-raw rule is about) -- so rendering it
              here is the one place in the app that's safe to do so. */}
          <Box
            css={{
              '& ul, & ol': { paddingLeft: '1.25rem' },
              '& h1, & h2, & h3': { fontWeight: 'bold', marginTop: '0.5rem' },
              '& a': { color: 'accent' },
            }}
            dangerouslySetInnerHTML={{ __html: preview.data.html }}
          />
        </Box>
      )}
      {!preview.data && !preview.isPending && !preview.error && (
        <Text fontSize="sm" color="fg.muted">
          Preview will appear here once the subject and message have content.
        </Text>
      )}
    </Card>
  );
}
